import { Text } from '@mantine/core';

import CardReader from '@/components/CardReader.tsx';
import CardReadModalFooter from '@/components/PatientModal/CardReadModalFooter.tsx';
import StackSelectionModal from '@/components/PatientModal/StackSelectionModal.tsx';
import TicketInputModal from '@/components/PatientModal/TicketInputModal.tsx';
import { PatientModal } from '@/components/PatientModal/patient-modal.tsx';
import TaskAssignModal from '@/components/TaskView/TaskAssignModal.tsx';
import WalkInModal from '@/components/WalkInModal.tsx';
import WalkInModalFooter from '@/components/WalkInModalFooter.tsx';
import { CollectionEditScopeModal } from '@/components/collection-edit-scope-modal.tsx';
import FormQRCode from '@/components/form/FormQRCode.tsx';

import { CardReadModalOptions } from '@/models/CardReader.ts';
import { ModalType } from '@/models/Modal.ts';
import { Appointment, CollectionActionScope, DailyAppointment } from '@/models/appointment/Appointment.ts';
import { StackSelectionResponse } from '@/models/call-system/StackSelection.ts';

import { closeModal, openConfirmModal, openModal } from '@/services/modalService.tsx';

import { usePatientModalStore } from '@/stores/patientModalStore.ts';
import { WalkInModalMode, WalkInTab, useWalkInStore } from '@/stores/walkInStore.ts';

const WALK_IN_MODAL_TITLES = {
    [WalkInModalMode.BOOK]: 'Termin erstellen',
    [WalkInModalMode.ADD_PATIENT_TO_APPOINTMENT]: 'Patient:in hinzufügen',
    [WalkInModalMode.CREATE_PATIENT]: 'Patient:in erstellen',
};

export const openPatientModal = (
    pvs_patient_id: string,
    eterno_patient_id: string | undefined,
    pat_instance_id: string,
    patient_name: string,
    appointment?: DailyAppointment,
    options?: {
        onClose?: () => void;
    }
): void => {
    usePatientModalStore.setState({ appointment });

    openModal({
        onClose: () => {
            if (options?.onClose) options.onClose();
        },
        title: patient_name,
        size: 'xl',
        classNames: {
            childrenWrapper: 'h-[75vh]',
        },
        children: (
            <PatientModal
                pvsPatientId={pvs_patient_id}
                eternoPatientId={eterno_patient_id}
                instanceId={pat_instance_id}
            />
        ),
    });
};

export const openQrCodeModal = (code: string, instance_id: string): void => {
    openModal({
        title: 'Lesen Sie den QR-Code',
        children: <FormQRCode code={code} instanceId={instance_id} />,
    });
};

export const openTicketNumberModal = (
    handleSave: (ticket: string) => Promise<void>,
    handleCancel?: () => Promise<void> | void
): void => {
    const onCancel = async (): Promise<void> => {
        closeModal(ModalType.TICKET_NUMBER);
        if (handleCancel) await handleCancel();
    };

    const onConfirm = async (ticket: string): Promise<void> => {
        closeModal(ModalType.TICKET_NUMBER);
        await handleSave(ticket);
    };

    openModal({
        modalId: ModalType.TICKET_NUMBER,
        title: 'Ticket ausdrucken',
        children: <TicketInputModal onCancel={onCancel} onConfirm={onConfirm} />,
        withCloseButton: false,
        closeOnEscape: false,
    });
};

export const openConfirmationModal = (
    text: string,
    title: string | undefined = 'Bestätigung',
    handleConfirm: () => Promise<void> | void,
    handleCancel?: () => Promise<void> | void
): void => {
    openConfirmModal({
        title: title,
        children: (
            <Text size="sm" className="px-4 py-2">
                {text}
            </Text>
        ),
        onCancel: handleCancel,
        onConfirm: handleConfirm,
        withCloseButton: false,
    });
};

export const openWalkInModal = (
    onConfirm: () => void,
    onClose?: () => void,
    mode: WalkInModalMode = WalkInModalMode.BOOK
): void => {
    const handleOnClose = (): void => {
        useWalkInStore.getState().resetAllData();
        if (onClose) onClose();
    };

    // Set default tab
    const defaultTab = mode === WalkInModalMode.CREATE_PATIENT ? WalkInTab.NEW_PATIENT : WalkInTab.SEARCH;
    useWalkInStore.setState({ active_tab: defaultTab });

    openModal({
        modalId: ModalType.WALK_IN,
        title: WALK_IN_MODAL_TITLES[mode],
        children: <WalkInModal onConfirm={onConfirm} mode={mode} />,
        onClose: handleOnClose,
        withCloseButton: true,
        closeOnEscape: true,
        footer: <WalkInModalFooter onConfirm={onConfirm} />,
    });
};

export const openTaskAssignModal = (
    instance_id: string,
    onConfirm: (id: string) => Promise<void>,
    onCancel: () => void
): void => {
    const handleClose = (): void => {
        closeModal(ModalType.TASK_ASSIGN);
    };

    openModal({
        modalId: ModalType.TASK_ASSIGN,
        title: 'Mitarbeiter zuweisen',
        children: (
            <TaskAssignModal onConfirm={onConfirm} onClose={handleClose} instanceId={instance_id} onCancel={onCancel} />
        ),
    });
};

export const openStackSelectionModal = (
    stacks: string[],
    onComplete: (response: StackSelectionResponse) => void
): void => {
    const handleComplete = (response: StackSelectionResponse): void => {
        closeModal(ModalType.STACK_SELECTION);
        onComplete(response);
    };

    // Pre-select if only one stack available
    const defaultSelectedStack = stacks.length === 1 ? stacks[0] : undefined;

    openModal({
        modalId: ModalType.STACK_SELECTION,
        title: 'Zimmer auswählen',
        size: 'sm',
        children: (
            <StackSelectionModal
                stacks={stacks}
                defaultSelectedStack={defaultSelectedStack}
                onConfirm={handleComplete}
            />
        ),
        withCloseButton: false,
        closeOnEscape: false,
        closeOnClickOutside: false,
    });
};

export const openCardReadModal = (options: CardReadModalOptions): void => {
    const { expectedPvsUserId, instanceId, customerId, onSuccess } = options;

    useWalkInStore.setState({
        card_read_state: undefined,
        selected_instance_id: instanceId,
    });

    const handleClose = (): void => {
        useWalkInStore.setState({
            card_read_state: undefined,
            selected_instance_id: undefined,
        });
    };

    openModal({
        modalId: ModalType.CARD_READ,
        title: 'eGK einlesen',
        size: 'lg',
        children: <CardReader />,
        onClose: handleClose,
        withCloseButton: true,
        closeOnEscape: true,
        footer: (
            <CardReadModalFooter expectedPvsUserId={expectedPvsUserId} customerId={customerId} onSuccess={onSuccess} />
        ),
    });
};

export const openCollectionEditScopeModal = (
    onConfirm: (scope: CollectionActionScope) => void,
    appointment?: Appointment,
    patientInstanceId?: string,
    onCancel?: () => void
): void => {
    const handleCancel = (): void => {
        closeModal(ModalType.COLLECTION_EDIT_SCOPE);
        if (onCancel) onCancel();
    };

    const handleConfirm = (scope: CollectionActionScope): void => {
        closeModal(ModalType.COLLECTION_EDIT_SCOPE);
        onConfirm(scope);
    };

    openModal({
        modalId: ModalType.COLLECTION_EDIT_SCOPE,
        title: 'Termin bearbeiten',
        size: 'md',
        children: (
            <CollectionEditScopeModal
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                isEditMode
                appointment={appointment}
                patientInstanceId={patientInstanceId}
            />
        ),
        withCloseButton: true,
        closeOnEscape: true,
    });
};
